import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Translate } from 'react-redux-i18n'
import EventEmitter, { SHOW_SELECT_CUSTOM } from '../../helpers/EventEmitter'
import { getUnique } from '../../helpers/common'
import { getInnerHeight } from '../../helpers/device'
import { HandleClickOutside } from '../HandleClickOutside'
import { Dropdown, positionDropdown } from '../dropdown'
import { DropdownList } from '../dropdown/DropdownList'
import { Element } from '../html/element'
import { Icon } from '../html/icon'
import { ScrollComponent } from '../scrollComponent'
import TextEllipsis from '../textEllipsis'
import classes from './index.module.scss'

export const SelectCustom = ({
  children,
  activeItem,
  chooseItem,
  className,
  style,
  data,
  iconNoPosition,
  disabled,
  isI18n,
  maxItem,
  renderActiveItem,
  renderDropdownItem,
  scrollElementIds,
  dropdownPosition,
  minWidthDropdownEqualSelect,
  zIndex,
  customWidthDropdown,
  labelOnBorder,
  scrollId,
  styleDropdown,
  iconWhenShowDropList,
  iconWhenHiddenDropList,
  isBorderNone,
  isNoWrap,
  handleClickOutside,
  handleOnClick,
  id,
  styleLabelOnBorder,
  hiddenIcon,
  isHiddenDropdownClick,
}) => {
  const selectRef = useRef()
  const [isShow, setIsShow] = useState(false)
  const [widthDropdown, setWidthDropdown] = useState(0)
  const [heightItem, setHeightItem] = useState(0)

  const idUnique = useRef(getUnique())

  useEffect(() => {
    onDidMount()
  }, [data])

  useEffect(() => {
    const eventAction = (data) => {
      if (id === data?.id) {
        setIsShow(true)
      }
    }

    EventEmitter.subscribe(SHOW_SELECT_CUSTOM, eventAction)
    return () => {
      EventEmitter.unsubscribe(SHOW_SELECT_CUSTOM, eventAction)
    }
  }, [])

  const onClick = (e) => {
    e?.stopPropagation()
    if (disabled) return
    if (isHiddenDropdownClick) {
      setIsShow(!isShow)
    } else {
      setIsShow(true)
    }
    handleOnClick(isShow)
  }

  const onClickItem = (item) => {
    if (disabled) return
    setIsShow(false)
    chooseItem(item)
  }

  const onDidMount = () => {
    if (data.length) {
      const draftDropdownEl = document.getElementById(idUnique.current)
      setWidthDropdown(
        Math.ceil(draftDropdownEl?.getBoundingClientRect()?.width),
      )
      setHeightItem(draftDropdownEl?.offsetHeight / data.length)
    }
  }

  const label = data.find((item) => item.value === activeItem?.value)?.label
  const heightDropdown =
    (data.length > maxItem ? maxItem : data.length) * heightItem

  const getPosition = () => {
    if (!selectRef.current) return positionDropdown.BOTTOM_START_RIGHT
    const windowHeight = getInnerHeight()
    const { top, bottom } = selectRef.current.getBoundingClientRect()
    return windowHeight - bottom < heightDropdown && top > windowHeight / 2
      ? positionDropdown.TOP_START_RIGHT
      : positionDropdown.BOTTOM_START_RIGHT
  }

  const getMaxHeightDropdown = () => {
    const windowHeight = getInnerHeight()
    if (!selectRef.current || dropdownPosition) return windowHeight
    const { top, bottom } = selectRef.current.getBoundingClientRect()
    const position = getPosition()
    return position === positionDropdown.BOTTOM_START_RIGHT
      ? windowHeight - bottom
      : top
  }

  // Hide dropdown when content scroll
  useEffect(() => {
    const hideDropdown = () => setIsShow(false)

    const scrollElements = scrollElementIds.map((id) =>
      document.querySelector(`#${id} .scrollbars div:first-child`),
    )

    scrollElements.forEach((element) => {
      if (element) element.addEventListener('scroll', hideDropdown)
    })

    return () => {
      scrollElements.forEach((element) => {
        if (element) element.removeEventListener('scroll', hideDropdown)
      })
    }
  }, [])

  // Hide dropdown when window resize
  useEffect(() => {
    const hideDropdown = () => setIsShow(false)

    window.addEventListener('resize', hideDropdown)

    return () => {
      window.removeEventListener('resize', hideDropdown)
    }
  }, [])

  return (
    <>
      <Element
        ref={selectRef}
        onClick={onClick}
        className={[className, classes.select, 'positionRelative'].join(' ')}
        style={{
          color: disabled ? '#9C9C9C' : 'inherit',
          border: isBorderNone ? 'none' : '1px solid #E0E0E0',
          padding: isBorderNone ? '0 27px 0 12px' : '4px 27px 4px 12px',
          fontSize: 12,
          ...style,
        }}
      >
        {labelOnBorder && (
          <Element
            elementName="p"
            style={{ backgroundColor: '#F3F3F3', ...styleLabelOnBorder }}
            className={classes.labelOnBorder}
          >
            <Translate value={labelOnBorder} />
          </Element>
        )}
        {renderActiveItem ? (
          renderActiveItem(activeItem)
        ) : label && typeof label === 'string' ? (
          <TextEllipsis text={label} isI18n={isI18n} zIndexTooltip={zIndex} />
        ) : (
          label
        )}
        {!hiddenIcon && (
          <>
            &nbsp;
            <Icon
              className={[
                isShow ? iconWhenShowDropList : iconWhenHiddenDropList,
                iconNoPosition ? 'ml1' : 'positionAbsolute',
              ].join(' ')}
              style={
                iconNoPosition
                  ? { fontSize: 8 }
                  : {
                      fontSize: 8,
                      right: 12,
                      top: '50%',
                      transform: 'translate(0, -50%)',
                      lineHeight: 0,
                    }
              }
            />
          </>
        )}
      </Element>
      {isShow && (
        <Dropdown
          parentRef={selectRef}
          position={dropdownPosition || getPosition()}
          zIndex={zIndex}
          scrollId={scrollId}
          setIsShow={setIsShow}
        >
          <HandleClickOutside
            exceptRef={selectRef}
            handleClickOutside={() => {
              setIsShow(false)
              handleClickOutside()
            }}
          >
            {children ? (
              children(onClickItem)
            ) : (
              <div
                style={{
                  height: `${heightDropdown}px`,
                  width: `${
                    customWidthDropdown ? customWidthDropdown : widthDropdown
                  }px`,
                  maxHeight: getMaxHeightDropdown(),
                  minWidth: minWidthDropdownEqualSelect
                    ? selectRef.current?.offsetWidth
                    : undefined,
                  ...styleDropdown,
                }}
                className={classes.dropdownContainer}
              >
                <ScrollComponent>
                  <DropdownList
                    activeItem={activeItem}
                    data={data}
                    onClickItem={onClickItem}
                    renderDropdownItem={renderDropdownItem}
                    isNoWrap={isNoWrap}
                  />
                </ScrollComponent>
              </div>
            )}
          </HandleClickOutside>
        </Dropdown>
      )}
      {data && !widthDropdown && (
        <div id={idUnique.current} className={classes.dropdownCalcWidth}>
          <DropdownList
            activeItem={activeItem}
            data={data}
            onDidMount={onDidMount}
            renderDropdownItem={renderDropdownItem}
            onClickItem={onClickItem}
            isNoWrap={isNoWrap}
          />
        </div>
      )}
    </>
  )
}

SelectCustom.propTypes = {
  children: PropTypes.func,
  activeItem: PropTypes.object,
  className: PropTypes.string,
  style: PropTypes.object,
  data: PropTypes.array.isRequired,
  dropDownStyle: PropTypes.object,
  iconNoPosition: PropTypes.bool,
  disabled: PropTypes.bool,
  isI18n: PropTypes.bool,
  maxItem: PropTypes.number,
  renderActiveItem: PropTypes.func,
  renderDropdownItem: PropTypes.func,
  scrollElementIds: PropTypes.array,
  dropdownPosition: PropTypes.string,
  minWidthDropdownEqualSelect: PropTypes.bool,
  zIndex: PropTypes.number,
  customWidthDropdown: PropTypes.number,
  scrollId: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  styleDropdown: PropTypes.object,
  iconWhenShowDropList: PropTypes.string,
  iconWhenHiddenDropList: PropTypes.string,
  isBorderNone: PropTypes.bool,
  isNoWrap: PropTypes.bool,
  chooseItem: PropTypes.func,
  handleClickOutside: PropTypes.func,
  handleOnClick: PropTypes.func,
  hiddenIcon: PropTypes.bool,
  isHiddenDropdownClick: PropTypes.bool,
}

SelectCustom.defaultProps = {
  className: '',
  style: {},
  disabled: false,
  isI18n: false,
  iconNoPosition: false,
  maxItem: 10,
  scrollElementIds: [],
  minWidthDropdownEqualSelect: true,
  styleDropdown: {},
  iconWhenShowDropList: 'icon-caret-top',
  iconWhenHiddenDropList: 'icon-caret-bottom',
  isBorderNone: false,
  isNoWrap: false,
  chooseItem: () => {},
  handleClickOutside: () => {},
  handleOnClick: () => {},
  hiddenIcon: false,
  isHiddenDropdownClick: true,
}
